// SMS — giden SMS istatistikleri (İstatistik) + gönderim günlüğü (Günlük).
// Veri /api/sms'ten (migration 022 sms_events); proxy /api/sms-send her gönderim denemesini
// (başarı VEYA başarısızlık) buraya relay eder. Marka kapsamı sunucuda uygulanır — süpervizör
// yalnız kendi markalarının gönderimlerini görür. Bu, alıcı telefonu + mesaj gövdesini gösteren
// tek sayfadır (sahibin kararı: tam denetim); kapsam kapısı çağrı günlüğüyle aynıdır.

import { useEffect, useMemo, useState } from 'react';
import { getSmsReport } from '../api';
import {
  brandLabel, smsOutcomeColor, smsOutcomeLabel, smsReasonLabel,
  smsTemplateColor, smsTemplateLabel, smsTemplateOptionIds, tzOfBrands,
} from '../constants';
import { computePreset, downloadCSV, fmtClock, fmtDate, fmtStamp } from '../format';
import type { BrandId, Lang, ReportPreset, SmsReport } from '../types';
import { cardStyle, CsvButton, dotStyle, EmptyState, Icon, Select, segStyle, Skeleton } from '../ui';

const PRESETS: [ReportPreset, string][] = [
  ['today', 'Bugün'],
  ['yesterday', 'Dün'],
  ['this_week', 'Bu Hafta'],
  ['last_week', 'Geçen Hafta'],
  ['this_month', 'Bu Ay'],
];

const dateFilterStyle = {
  background: 'var(--inset)',
  border: '1px solid var(--border)',
  borderRadius: '9px',
  padding: '8px 10px',
  color: 'var(--text)',
  fontSize: '12.5px',
  outline: 'none',
  colorScheme: 'dark',
} as const;

type View = 'stats' | 'log';

// SVG donut için: r=54 → çevre ≈ 339.29. Her dilim stroke-dasharray + kümülatif offset ile çizilir.
const DONUT_R = 54;
const DONUT_C = 2 * Math.PI * DONUT_R;

function KpiTile({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ ...cardStyle, padding: '14px 16px', flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '10.5px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: color || 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

export default function Sms({
  brands,
  onGoAgent,
}: {
  brands: BrandId[];
  // `lang` App'ten geçer (kabuk tutarlılığı) ama bu sayfa EN kaplamasını i18n DOM katmanından
  // alır — burada okunmaz.
  lang: Lang;
  onGoAgent: (id: string) => void;
}) {
  // Dönem "bugün"ü marka diliminde (BetBooker=Akra) — tarayıcı saati değil.
  const brandTz = tzOfBrands(brands);
  const [view, setView] = useState<View>('stats');
  const [preset, setPreset] = useState<ReportPreset>('today');
  const [start, setStart] = useState(() => computePreset('today', brandTz).start);
  const [end, setEnd] = useState(() => computePreset('today', brandTz).end);
  // Süzgeçler yalnız Günlük görünümünde uygulanır — İstatistik her zaman tüm kümeyi gösterir.
  const [outcome, setOutcome] = useState<'' | 'sent' | 'failed'>('');
  const [templateId, setTemplateId] = useState('');
  const [data, setData] = useState<SmsReport | null>(null);
  // Şablon süzgecinin seçenekleri için görülen id'ler — yalnız eklenir: süzgeç uygulanınca rapor tek
  // şablona daralır, ama diğer (ör. Prime VIP) şablonlar seçenek listesinden düşmemeli.
  const [seenTemplates, setSeenTemplates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const brandKey = brands.join(',');
  const useFilters = view === 'log';

  useEffect(() => {
    const range = preset === 'custom' ? { start, end } : computePreset(preset, brandTz);
    if (!range.start || !range.end) return;
    let alive = true;
    setLoading(true);
    getSmsReport(brands, {
      preset: preset === 'custom' ? undefined : preset,
      start: preset === 'custom' ? start : undefined,
      end: preset === 'custom' ? end : undefined,
      outcome: useFilters && outcome ? outcome : undefined,
      templateId: useFilters && templateId ? templateId : undefined,
    }).then(
      (d) => {
        if (!alive) return;
        setData(d);
        setSeenTemplates((prev) => {
          const next = new Set(prev);
          for (const t of d?.stats?.byTemplate || []) if (t.templateId) next.add(t.templateId);
          for (const r of d?.logs || []) if (r.templateId) next.add(r.templateId);
          return next.size === prev.length ? prev : [...next];
        });
        setLoading(false);
      },
      () => {
        if (!alive) return;
        setData(null);
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, preset, start, end, outcome, templateId, brandKey]);

  const onPreset = (p: ReportPreset) => {
    const rng = computePreset(p, brandTz);
    setPreset(p);
    setStart(rng.start);
    setEnd(rng.end);
  };
  const onStart = (v: string) => {
    setPreset('custom');
    setStart(v);
  };
  const onEnd = (v: string) => {
    setPreset('custom');
    setEnd(v);
  };

  const range = preset === 'custom' ? { start, end } : computePreset(preset, brandTz);
  const rangeLabel = fmtDate(range.start) + ' – ' + fmtDate(range.end);

  const stats = data?.stats;

  // Donut dilimleri: şablon başına toplam gönderim (renk şablonun kendi rengi).
  const donut = useMemo(() => {
    const items = (stats?.byTemplate || []).filter((t) => t.total > 0);
    const sum = items.reduce((n, t) => n + t.total, 0);
    let acc = 0;
    const slices = items.map((t) => {
      const frac = sum ? t.total / sum : 0;
      const len = frac * DONUT_C;
      const slice = { templateId: t.templateId, total: t.total, len, offset: -acc };
      acc += len;
      return slice;
    });
    return { slices, sum };
  }, [stats]);

  const reasonMax = Math.max(1, ...(stats?.byReason || []).map((r) => r.count));

  const onCsv = () => {
    const head = ['Saat', 'Temsilci', 'Marka', 'Musteri ID', 'Alici', 'Sablon', 'Durum', 'Sebep', 'Uzunluk', 'Mesaj'];
    downloadCSV(
      'sms-gunlugu.csv',
      [head as (string | number)[]].concat(
        (data?.logs || []).map((r) => [
          fmtStamp(r.at, brandTz),
          r.name,
          brandLabel(r.brand),
          r.customerId,
          r.phone,
          smsTemplateLabel(r.templateId),
          smsOutcomeLabel(r.outcome),
          r.outcome === 'failed' ? smsReasonLabel(r.reason) : '',
          r.msgLen,
          r.body,
        ]),
      ),
    );
  };

  const loadingNow = loading && !data;
  const emptyReason =
    brands.length === 0
      ? 'Üstteki marka filtresinden en az bir marka seçin.'
      : 'Seçili dönem ve markalar için SMS kaydı bulunamadı.';
  const statsEmpty = !!stats && stats.total === 0;
  const logsEmpty = !!data && data.logs.length === 0;

  const logCols = '82px 150px 118px 132px 128px 118px 1fr';

  return (
    <>
      {/* görünüm + dönem seçimi */}
      <div style={{ ...cardStyle, padding: '16px 18px', marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <button onClick={() => setView('stats')} style={segStyle(view === 'stats')}>İstatistik</button>
          <button onClick={() => setView('log')} style={segStyle(view === 'log')}>Kayıtlar</button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--dim)', fontSize: '12.5px' }}>
            <Icon name="event" size={17} color="var(--faint)" />
            <span>{rangeLabel}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.06em', color: 'var(--faint)', marginRight: '2px' }}>DÖNEM</span>
          {PRESETS.map(([k, l]) => (
            <button key={k} onClick={() => onPreset(k)} style={segStyle(preset === k)}>
              {l}
            </button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '6px' }}>
            <input type="date" value={start} onChange={(e) => onStart(e.target.value)} style={dateFilterStyle} />
            <span style={{ color: 'var(--faint)' }}>–</span>
            <input type="date" value={end} onChange={(e) => onEnd(e.target.value)} style={dateFilterStyle} />
          </div>
        </div>
      </div>

      {loadingNow && <Skeleton />}

      {/* ── İSTATİSTİK ── */}
      {!loadingNow && view === 'stats' && stats && !statsEmpty && (
        <>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' }}>
            <KpiTile label="TOPLAM GÖNDERİM" value={String(stats.total)} />
            <KpiTile label="GÖNDERİLDİ" value={String(stats.sent)} color="#37c46b" />
            <KpiTile label="BAŞARISIZ" value={String(stats.failed)} color={stats.failed > 0 ? '#ef5d5d' : undefined} />
            <KpiTile label="BAŞARI ORANI" value={'%' + stats.successRate} color="#3d8bff" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: '14px', marginBottom: '18px' }}>
            {/* şablon dağılımı (donut) + başarısızlık sebepleri — tek kart, iki eşit yarım */}
            <div style={{ ...cardStyle, padding: '16px 18px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>Şablon dağılımı</div>
              <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '14px' }}>Gönderim şablonuna göre</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', flex: 1 }}>
                <svg viewBox="0 0 140 140" style={{ width: '140px', height: '140px', flex: 'none', transform: 'rotate(-90deg)' }}>
                  <circle cx="70" cy="70" r={DONUT_R} fill="none" style={{ stroke: 'var(--inset)' }} strokeWidth="16" />
                  {donut.slices.map((s) => (
                    <circle
                      key={s.templateId}
                      cx="70"
                      cy="70"
                      r={DONUT_R}
                      fill="none"
                      stroke={smsTemplateColor(s.templateId)}
                      strokeWidth="16"
                      strokeDasharray={`${s.len} ${DONUT_C - s.len}`}
                      strokeDashoffset={s.offset}
                    />
                  ))}
                </svg>
                <div style={{ flex: 1, minWidth: '150px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {stats.byTemplate.map((t) => (
                    <div key={t.templateId} style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                      <span style={dotStyle(smsTemplateColor(t.templateId), 9)} />
                      <span style={{ flex: 1, fontSize: '12px', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {smsTemplateLabel(t.templateId)}
                      </span>
                      <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{t.total}</span>
                      {t.failed > 0 && (
                        <span style={{ fontSize: '11px', color: '#ef6d6d', fontVariantNumeric: 'tabular-nums' }}>{'· ' + t.failed}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              </div>

              <div style={{ borderTop: '1px solid var(--border)', margin: '16px -18px' }} />

              {/* başarısızlık sebepleri — alt yarım */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>Başarısızlık sebepleri</div>
                <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '14px' }}>Başarısız gönderimlerin dağılımı</div>
                {stats.byReason.length === 0 ? (
                  <div style={{ fontSize: '12.5px', color: 'var(--dim)', padding: '14px 0' }}>Başarısız gönderim yok</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto' }}>
                    {stats.byReason.map((r) => (
                      <div key={r.reason} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span
                          style={{
                            width: '120px',
                            flex: 'none',
                            fontSize: '11.5px',
                            color: 'var(--dim)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {smsReasonLabel(r.reason)}
                        </span>
                        <div style={{ flex: 1, height: '12px', background: 'var(--inset)', borderRadius: '5px', overflow: 'hidden' }}>
                          <div style={{ width: (r.count / reasonMax) * 100 + '%', height: '100%', background: '#ef5d5d', borderRadius: '5px' }} />
                        </div>
                        <span style={{ width: '40px', flex: 'none', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                          {r.count}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* temsilci başına */}
            <div style={{ ...cardStyle, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
                <Icon name="groups" size={19} color="var(--accent)" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>Temsilci başına</div>
                </div>
                <span style={{ fontSize: '11.5px', color: 'var(--faint)' }}>{stats.byAgent.length + ' temsilci'}</span>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 92px 92px 84px',
                  gap: '10px',
                  padding: '9px 18px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '.04em',
                  color: 'var(--faint)',
                }}
              >
                <span>TEMSİLCİ</span>
                <span style={{ textAlign: 'right' }}>GÖNDERİLDİ</span>
                <span style={{ textAlign: 'right' }}>BAŞARISIZ</span>
                <span style={{ textAlign: 'right' }}>BAŞARI</span>
              </div>
              <div style={{ maxHeight: '460px', overflowY: 'auto' }}>
                {stats.byAgent.map((a) => {
                  const rate = a.total ? Math.round((100 * a.sent) / a.total) : 0;
                  return (
                    <div
                      key={a.agentId}
                      onClick={() => onGoAgent(a.agentId)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 92px 92px 84px',
                        gap: '10px',
                        padding: '11px 18px',
                        borderBottom: '1px solid var(--border)',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {a.name}
                      </span>
                      <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: '#37c46b', fontVariantNumeric: 'tabular-nums' }}>{a.sent}</span>
                      <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: a.failed > 0 ? '#ef6d6d' : 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>
                        {a.failed}
                      </span>
                      <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{'%' + rate}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {!loadingNow && view === 'stats' && (statsEmpty || !stats) && (
        <EmptyState icon="sms" title="SMS kaydı yok" reason={emptyReason} />
      )}

      {/* Log görünümü, veri gelmediyse (ağ hatası) de boş durum gösterir — beyaz ekran bırakmaz. */}
      {!loadingNow && view === 'log' && !data && <EmptyState icon="sms" title="SMS kaydı yok" reason={emptyReason} />}

      {/* ── GÜNLÜK ── */}
      {!loadingNow && view === 'log' && data && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <Select
              compact
              value={outcome}
              onChange={(v) => setOutcome(v as '' | 'sent' | 'failed')}
              options={[
                { value: '', label: 'Tüm sonuçlar' },
                { value: 'sent', label: 'Gönderildi' },
                { value: 'failed', label: 'Başarısız' },
              ]}
            />
            <Select
              compact
              value={templateId}
              onChange={setTemplateId}
              options={[{ value: '', label: 'Tüm şablonlar' }].concat(
                smsTemplateOptionIds(seenTemplates, templateId).map((id) => ({ value: id, label: smsTemplateLabel(id) })),
              )}
            />
            <div style={{ marginLeft: 'auto' }}>
              <CsvButton onClick={onCsv} />
            </div>
          </div>

          {logsEmpty ? (
            <EmptyState icon="sms" title="SMS kaydı yok" reason={emptyReason} />
          ) : (
            <div style={{ ...cardStyle, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 18px', borderBottom: '1px solid var(--border)' }}>
                <Icon name="outgoing_mail" size={19} color="var(--accent)" />
                <div style={{ flex: 1, fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>Gönderim günlüğü</div>
                <span style={{ fontSize: '12px', color: 'var(--dim)' }}>
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>{data.logs.length}</span> kayıt
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: '900px' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: logCols,
                      gap: '10px',
                      padding: '9px 18px',
                      borderBottom: '1px solid var(--border)',
                      fontSize: '10px',
                      fontWeight: 600,
                      letterSpacing: '.04em',
                      color: 'var(--faint)',
                    }}
                  >
                    <span>SAAT</span>
                    <span>TEMSİLCİ</span>
                    <span>MÜŞTERİ ID</span>
                    <span>ALICI</span>
                    <span>ŞABLON</span>
                    <span>SONUÇ</span>
                    <span>MESAJ</span>
                  </div>
                  <div style={{ maxHeight: '560px', overflowY: 'auto' }}>
                    {data.logs.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: logCols,
                          gap: '10px',
                          padding: '10px 18px',
                          borderBottom: '1px solid var(--border)',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{fmtClock(r.at, brandTz)}</span>
                        <span
                          onClick={() => onGoAgent(r.agentId)}
                          style={{ fontSize: '12px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
                          title={brandLabel(r.brand)}
                        >
                          {r.name}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{r.customerId}</span>
                        <span style={{ fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{r.phone}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text)', minWidth: 0 }}>
                          <span style={dotStyle(smsTemplateColor(r.templateId))} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{smsTemplateLabel(r.templateId)}</span>
                        </span>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: smsOutcomeColor(r.outcome),
                            minWidth: 0,
                          }}
                          title={r.outcome === 'failed' ? smsReasonLabel(r.reason) : ''}
                        >
                          <span style={dotStyle(smsOutcomeColor(r.outcome))} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.outcome === 'failed' && r.reason ? smsOutcomeLabel(r.outcome) + ' · ' + smsReasonLabel(r.reason) : smsOutcomeLabel(r.outcome)}
                          </span>
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.body}>
                          {r.body}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {data.logTruncated && (
                <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', fontSize: '11.5px', color: 'var(--faint)' }}>
                  {'En yeni ' + data.logs.length + ' kayıt gösteriliyor — daraltmak için dönem veya süzgeç kullanın'}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
